import { Link } from "react-router-dom";
import { Phone, MessageCircle, CalendarClock, MapPin, Clock, ArrowRight, ShieldCheck, Scan, Cpu, Microscope, Waves, Sparkles, Anchor, Layers, Smile, Siren, AlignCenter, Activity, Search as SearchIcon, ScanLine, Grid3x3, Stethoscope, ClipboardCheck, FileText, Wrench, RefreshCw, CheckCircle2 } from "lucide-react";
import { useEffect, useState, lazy, Suspense } from "react";
import { api, CLINIC } from "@/lib/api";
import PageLayout from "@/components/layout/PageLayout";
import { CTASection, TrustBadges, Disclaimer } from "@/components/common/Sections";
import ImagePlaceholder from "@/components/common/ImagePlaceholder";
import BrandedVisual from "@/components/common/BrandedVisual";
import InitialsAvatar from "@/components/common/InitialsAvatar";
import BrandLogos from "@/components/common/BrandLogos";
import { HOMEPAGE_BRANDS } from "@/data/brands";
// Reviews are below-the-fold on the homepage — lazy-load the chunk so it
// doesn't ship in the initial main.js bundle. Cuts a few KB off the
// critical path without changing layout or visible content.
const ReviewsSection = lazy(() => import("@/components/common/ReviewsSection"));

const iconMap = { Anchor, Smile, Layers, ScanLine, SearchIcon, Sparkles, Activity, AlignCenter, Siren, Grid3x3, Stethoscope };

export default function Home() {
  const [services, setServices] = useState([]);
  const [doctors, setDoctors] = useState([]);

  useEffect(() => {
    api.services().then(setServices).catch(() => {});
    api.doctors().then(setDoctors).catch(() => {});
  }, []);

  // JSON-LD — Dentist + Organization + WebSite (site-wide entity anchor).
  // Only verified clinic information. No ratings, reviews or credentials.
  const homeJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Dentist",
      "@id": "https://www.dentiservice.ro/#dentist",
      name: "Denti Service",
      alternateName: "Clinica Denti Service Bacău",
      url: "https://www.dentiservice.ro",
      image: "https://www.dentiservice.ro/og-dentiservice.png",
      logo: "https://www.dentiservice.ro/logo.png",
      telephone: "+40749153874",
      email: "contact@dentiservice.ro",
      priceRange: "$$",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Strada Ștefan cel Mare nr. 34",
        addressLocality: "Bacău",
        addressCountry: "RO",
      },
      openingHoursSpecification: [
        {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          opens: "08:00",
          closes: "20:00",
        },
      ],
      foundingDate: "1992",
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": "https://www.dentiservice.ro/#website",
      url: "https://www.dentiservice.ro",
      name: "Denti Service Bacău",
      inLanguage: "ro-RO",
      publisher: { "@id": "https://www.dentiservice.ro/#dentist" },
    },
  ];

  return (
    <PageLayout
      title="Dentist Bacău"
      description="Dentist în Bacău la Denti Service, clinică stomatologică din 1992. Consultații, implanturi, CEREC, endodonție, ortodonție și radiologie digitală."
    >
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(homeJsonLd) }}
      />
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        {/* subtle decorative background */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0">
          <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full opacity-40"
               style={{ background: "radial-gradient(circle, rgba(20,184,166,0.16) 0%, transparent 65%)" }}></div>
          <div className="absolute -top-20 right-[-10%] w-[560px] h-[560px] rounded-full opacity-40"
               style={{ background: "radial-gradient(circle, rgba(15,44,89,0.10) 0%, transparent 65%)" }}></div>
          <div className="absolute inset-0 opacity-[0.035]"
               style={{ backgroundImage: "linear-gradient(#0F2C59 1px, transparent 1px), linear-gradient(90deg, #0F2C59 1px, transparent 1px)", backgroundSize: "60px 60px" }}></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 pt-6 md:pt-16 pb-8 md:pb-12">
        <div className="grid gap-8 md:gap-14 items-center">
          <div className="relative lg:order-1">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/70 backdrop-blur border border-[#0F2C59]/10 text-xs sm:text-sm font-semibold text-[#0F2C59] shadow-sm" data-testid="hero-since-badge">
              <ShieldCheck className="w-4 h-4 text-[#14B8A6]" />
              Clinică stomatologică din 1992 · Bacău
            </div>
            <h1 className="mt-5 font-display font-extrabold text-[2rem] leading-[1.1] sm:text-5xl lg:text-6xl text-[#0F2C59] sm:leading-[1.05]">
              Clinică stomatologică modernă în Bacău
            </h1>
            <p className="mt-4 text-slate-600 text-sm md:text-lg leading-relaxed max-w-xl">
              Tratamente dentare personalizate, tehnologie digitală și investigații realizate în aceeași clinică.
            </p>

            {/* Hero CTAs — desktop only (lg+). On mobile/tablet the
                MobileStickyBar (Sună acum · Programare) plus the floating
                WhatsAppFab replace these buttons, so we hide them here to
                avoid duplicate primary actions on small screens. */}
            <div className="mt-6 md:mt-8 hidden lg:flex lg:flex-wrap gap-3">
              <a href={CLINIC.phoneTel} className="inline-flex items-center justify-center gap-2 bg-[#0F2C59] text-white rounded-full px-5 py-3.5 sm:px-6 sm:py-4 text-sm sm:text-base font-semibold hover:bg-[#163d7a] transition-colors" data-testid="hero-call-btn">
                <Phone className="w-4 h-4" /> Sună acum · {CLINIC.phone}
              </a>
              <a href={CLINIC.whatsapp} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white rounded-full px-5 py-3.5 sm:px-6 sm:py-4 text-sm sm:text-base font-semibold hover:bg-[#128C7E] transition-colors" data-testid="hero-whatsapp-btn">
                <MessageCircle className="w-4 h-4" /> Scrie pe WhatsApp
              </a>
              <Link to="/contact#programare" className="inline-flex items-center justify-center gap-2 bg-[#B4262A] text-white rounded-full px-5 py-3.5 sm:px-6 sm:py-4 text-sm sm:text-base font-semibold hover:bg-[#8E1D20] transition-colors" data-testid="hero-appointment-btn">
                <CalendarClock className="w-4 h-4" /> Solicită o programare
              </Link>
            </div>

            <div className="mt-6 md:mt-10 grid gap-2 text-sm text-slate-700">
              <div className="flex items-start gap-3"><MapPin className="w-4 h-4 text-[#0F2C59] mt-0.5 shrink-0" /> <span>{CLINIC.address}</span></div>
              <div className="flex items-center gap-3"><Clock className="w-4 h-4 text-[#0F2C59] shrink-0" /> {CLINIC.hours}</div>
            </div>
          </div>
        </div>
        </div>
      </section>

      {/* TRUST INTRO */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-8 md:py-14" data-testid="trust-section">
        <div className="grid gap-10 lg:grid-cols-5 items-start">
          <div className="lg:col-span-2">
            <div className="text-[#D97757] text-sm font-semibold uppercase tracking-widest">Despre clinică</div>
            <h2 className="mt-3 font-display font-extrabold text-3xl md:text-4xl text-[#0F2C59]">
              Stomatologie în Bacău din 1992
            </h2>
          </div>
          <div className="lg:col-span-3">
            <p className="text-slate-600 text-base md:text-lg leading-relaxed">
              Denti Service funcționează în Bacău din anul 1992. De-a lungul timpului, clinica s-a dezvoltat prin integrarea mai multor specialități stomatologice și a tehnologiilor moderne de diagnostic și tratament.
            </p>
            <p className="mt-3 text-slate-600 text-base md:text-lg leading-relaxed">
              Accentul rămâne pe evaluarea corectă a fiecărui caz și pe stabilirea unui plan de tratament adaptat pacientului.
            </p>
            <div className="mt-6"><TrustBadges /></div>
          </div>
        </div>
      </section>

      {/* Services grid entry point — footer #services-section anchor */}
      <section id="services-section" className="max-w-7xl mx-auto px-5 md:px-8 py-8 md:py-14" data-testid="services-section">
        <div className="flex items-end justify-between mb-8 md:mb-12 flex-wrap gap-4">
          <div>
            <div className="text-[#D97757] text-sm font-semibold uppercase tracking-widest">Servicii</div>
            <h2 className="mt-3 font-display font-extrabold text-3xl md:text-4xl text-[#0F2C59]">Ce tratamente oferim</h2>
          </div>
          <Link to="/tarife" className="text-sm font-semibold text-[#0F2C59] hover:underline inline-flex items-center gap-1">
            Vezi tarifele orientative <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:gap-5 md:gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((s, idx) => {
            const Icon = iconMap[s.icon] || Stethoscope;
            const grads = [
              "from-[#0F2C59] to-[#1E3A8A]",
              "from-[#155E75] to-[#0F2C59]",
              "from-[#0E4C57] to-[#0F2C59]",
            ];
            const g = grads[idx % grads.length];
            // Presentation-layer slug override — keep Home links consistent
            // with the established canonical pillar URL for All-on-4/6 even
            // if the CMS still uses a legacy slug. No DB write.
            const canonicalSlug = s.slug === "dinti-ficsi-all-on-4-all-on-6"
              ? "dinti-ficsi-pe-implanturi-bacau/"
              : s.slug;
            return (
              <Link key={s.id} to={`/${canonicalSlug}`} className="group relative bg-white border border-slate-200 rounded-2xl p-6 md:p-7 hover:-translate-y-1 hover:border-[#0F2C59] hover:shadow-lg transition-all overflow-hidden" data-testid={`service-card-${s.slug}`}>
                <div className="absolute -right-10 -top-10 w-32 h-32 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                     style={{ background: "radial-gradient(circle, rgba(20,184,166,0.15) 0%, transparent 70%)" }}></div>
                <div className={`relative w-14 h-14 rounded-2xl bg-gradient-to-br ${g} text-white grid place-items-center shadow-sm`}>
                  <Icon className="w-7 h-7" />
                </div>
                <h3 className="mt-5 font-display font-bold text-lg text-[#0F2C59]">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.short}</p>
                <span className="inline-flex items-center gap-1 mt-4 text-sm font-semibold text-[#0F2C59] group-hover:underline">
                  Vezi detalii <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            );
          })}
        </div>
        <div className="mt-8 md:mt-10 flex justify-center">
          <Link
            to="/servicii"
            className="inline-flex items-center justify-center gap-2 bg-[#0F2C59] text-white rounded-full px-6 py-3.5 text-sm sm:text-base font-semibold hover:bg-[#163d7a] transition-colors"
            data-testid="home-services-all-btn"
          >
            Vezi toate serviciile <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* WHY DENTI SERVICE — new patient-benefit oriented 4-card grid */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-8 md:py-14" data-testid="why-denti-section" aria-labelledby="why-denti-heading">
        <div className="max-w-3xl">
          <div className="text-[#D97757] text-sm font-semibold uppercase tracking-widest">De ce Denti Service</div>
          <h2 id="why-denti-heading" className="mt-3 font-display font-extrabold text-3xl md:text-4xl text-[#0F2C59] leading-tight">
            De ce Denti Service?
          </h2>
          <p className="mt-4 text-slate-600 text-base md:text-lg leading-relaxed">
            Un tratament stomatologic nu înseamnă întotdeauna rezolvarea unei singure probleme. În anumite cazuri, diagnosticul, tratamentul și restaurarea finală trebuie corelate între mai multe specialități și realizate într-o ordine potrivită situației pacientului.
          </p>
          <p className="mt-3 text-slate-600 text-base md:text-lg leading-relaxed">
            La Denti Service, aceste etape pot fi organizate în aceeași clinică, în funcție de nevoile medicale ale fiecărui caz.
          </p>
        </div>

        <div className="mt-8 md:mt-10 grid gap-4 md:gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { icon: ShieldCheck, title: "Din 1992 în Bacău", text: "Denti Service funcționează în Bacău din anul 1992, acumulând experiență clinică în diagnosticul și tratamentul unei game largi de probleme dentare." },
            { icon: Layers, title: "Mai multe specialități în aceeași clinică", text: "Atunci când un caz necesită implicarea mai multor specialități, etapele pot fi coordonate într-un plan comun de tratament." },
            { icon: Scan, title: "Diagnostic și planificare digitală", text: "Examinarea clinică poate fi completată, atunci când este indicat, cu radiologie digitală, CBCT, scanare intraorală și alte instrumente de diagnostic și planificare." },
            { icon: ClipboardCheck, title: "Plan de tratament personalizat", text: "Recomandările sunt stabilite după consultație și investigațiile necesare, în funcție de situația fiecărui pacient, nu după o soluție standard aplicată tuturor." },
          ].map(({ icon: Icon, title, text }) => (
            <div key={title} className="bg-white border border-slate-200 rounded-2xl p-5 md:p-6 hover:border-[#0F2C59] hover:shadow-sm transition-all" data-testid={`why-denti-card-${title.split(' ')[0].toLowerCase()}`}>
              <div className="w-11 h-11 rounded-xl bg-slate-100 grid place-items-center text-[#0F2C59]">
                <Icon className="w-5 h-5" />
              </div>
              <h3 className="mt-4 font-display font-bold text-[#0F2C59] leading-tight">{title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* IMPLANTS + FIXED TEETH */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-8 md:py-14 grid gap-6 lg:gap-8 lg:grid-cols-2" data-testid="implants-section">
        <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden flex flex-col md:grid md:grid-cols-5">
          <div className="p-6 sm:p-8 md:p-10 md:col-span-3 md:order-2">
            <div className="text-[#D97757] text-xs sm:text-sm font-semibold uppercase tracking-widest">Implanturi dentare</div>
            <h2 className="mt-3 font-display font-extrabold text-xl sm:text-2xl md:text-3xl text-[#0F2C59]">Planificare digitală și evaluare CBCT</h2>
            <p className="mt-3 text-sm md:text-base text-slate-600 leading-relaxed">
              Tratamentul cu implant dentar include planificare, evaluare CBCT, etapa chirurgicală și etapa protetică. Inserarea implanturilor este realizată de Dr. Zamfirache. Costul complet se stabilește după consultație, investigații și planul de tratament.
            </p>
            <Disclaimer>
              Prețul de 350 euro se referă la implantul propriu-zis și nu reprezintă costul complet al tratamentului.
            </Disclaimer>
            <Link to="/implant-dentar-bacau" className="inline-flex items-center gap-2 mt-2 text-[#0F2C59] font-semibold hover:underline" data-testid="link-implants">
              Detalii despre implanturi dentare în Bacău <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="md:col-span-2 md:order-1 bg-slate-50 flex items-center justify-center p-4 md:p-6">
            <picture>
              <source
                type="image/webp"
                srcSet={`${process.env.REACT_APP_BACKEND_URL}/api/media/service-implant-digital-400.webp 400w, ${process.env.REACT_APP_BACKEND_URL}/api/media/service-implant-digital-800.webp 800w`}
                sizes="(min-width: 768px) 300px, 192px"
              />
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/media/service-implant-digital-fallback.jpg`}
                alt="Implantologie digitală ghidată — flux de planificare"
                width="800"
                height="800"
                loading="lazy"
                decoding="async"
                /* aspect-square + max-h + w-auto keeps the img element at its
                   intrinsic 1:1 aspect (matches HTML width/height), so the
                   Lighthouse "displays images with incorrect aspect ratio"
                   audit no longer flags a mismatch between the 800×800 source
                   and a stretched rendered box. Visual identical: object-
                   contain still applies, letterbox now handled by the parent
                   flex container. */
                className="aspect-square w-auto h-auto max-h-52 max-w-full object-contain mx-auto md:max-h-[300px]"
              />
            </picture>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden flex flex-col md:grid md:grid-cols-5">
          <div className="p-6 sm:p-8 md:p-10 md:col-span-3 md:order-1">
            <div className="text-[#D97757] text-xs sm:text-sm font-semibold uppercase tracking-widest">Dinți ficși</div>
            <h2 className="mt-3 font-display font-extrabold text-xl sm:text-2xl md:text-3xl text-[#0F2C59]">All-on-4 și All-on-6</h2>
            <p className="mt-3 text-sm md:text-base text-slate-600 leading-relaxed">
              Reabilitare completă pe implanturi. Eligibilitatea se determină după consultație și CBCT. Tratamentul poate implica mai multe etape, iar durata depinde de situația clinică individuală.
            </p>
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-4 text-sm">
              <div><div className="text-slate-500">All-on-4</div><div className="font-display font-extrabold text-lg sm:text-xl text-[#0F2C59]">3.100 €</div><div className="text-xs text-slate-500 mt-1">4 implanturi + lucrare provizorie fixă</div></div>
              <div><div className="text-slate-500">All-on-6</div><div className="font-display font-extrabold text-lg sm:text-xl text-[#0F2C59]">3.865 €</div><div className="text-xs text-slate-500 mt-1">6 implanturi + lucrare provizorie fixă</div></div>
            </div>
            <p className="mt-3 text-xs text-slate-600 leading-relaxed">Planul final și costurile exacte se stabilesc după consultație și investigațiile realizate în clinică.</p>
            <Link to="/dinti-ficsi-pe-implanturi-bacau/" className="inline-flex items-center gap-2 mt-4 text-[#0F2C59] font-semibold hover:underline" data-testid="link-allon">
              Vezi detalii <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="md:col-span-2 md:order-2 bg-slate-50 flex items-center justify-center p-4 md:p-0">
            <picture>
              <source
                type="image/webp"
                srcSet={`${process.env.REACT_APP_BACKEND_URL}/api/media/service-cerec.webp`}
              />
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/media/service-cerec.jpg`}
                alt="Protetică digitală CEREC realizată în clinică"
                width="768"
                height="432"
                loading="lazy"
                decoding="async"
                className="max-h-52 md:max-h-none md:w-full md:h-full md:object-cover md:min-h-[280px]"
              />
            </picture>
          </div>
        </div>
      </section>

      {/* HOW TREATMENT BEGINS — 7-step mobile-friendly timeline */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-8 md:py-14" data-testid="how-treatment-section" aria-labelledby="how-treatment-heading">
        <div className="max-w-3xl">
          <div className="text-[#D97757] text-sm font-semibold uppercase tracking-widest">Pașii tratamentului</div>
          <h2 id="how-treatment-heading" className="mt-3 font-display font-extrabold text-3xl md:text-4xl text-[#0F2C59] leading-tight">
            Cum începe tratamentul la Denti Service?
          </h2>
          <p className="mt-4 text-slate-600 text-base md:text-lg leading-relaxed">
            Un plan de tratament corect începe cu înțelegerea situației medicale a pacientului și cu stabilirea etapelor necesare.
          </p>
        </div>

        <ol className="mt-8 md:mt-10 relative border-l-2 border-slate-200 pl-6 space-y-6" data-testid="how-treatment-steps">
          {[
            { icon: MessageCircle, title: "Programarea", text: "Pacientul poate solicita o consultație telefonic, prin WhatsApp sau prin formularul de programare." },
            { icon: Stethoscope, title: "Consultația", text: "Medicul discută problema principală, istoricul relevant și examinează situația dentară." },
            { icon: Scan, title: "Investigațiile necesare", text: "În funcție de caz, pot fi recomandate radiografii, CBCT, scanare intraorală sau alte investigații. Nu fiecare pacient are nevoie de toate aceste examinări." },
            { icon: Cpu, title: "Diagnosticul și planificarea", text: "Informațiile obținute prin consultație și investigații sunt analizate împreună pentru stabilirea opțiunilor de tratament." },
            { icon: FileText, title: "Planul de tratament", text: "Pacientului îi sunt explicate etapele recomandate, alternativele disponibile, durata estimată și costurile relevante." },
            { icon: Wrench, title: "Tratamentul", text: "Procedurile sunt realizate în succesiunea stabilită medical, în funcție de complexitatea cazului." },
            { icon: RefreshCw, title: "Monitorizarea", text: "După tratament, controalele și măsurile de întreținere sunt stabilite în funcție de procedurile efectuate și de nevoile pacientului." },
          ].map(({ icon: Icon, title, text }, i) => (
            <li key={title} className="relative" data-testid={`treatment-step-${i + 1}`}>
              <span
                aria-hidden="true"
                className="absolute -left-[calc(1.5rem+0.5rem+1px)] top-0 w-8 h-8 rounded-full bg-[#0F2C59] text-white grid place-items-center text-xs font-bold border-2 border-white shadow-sm"
              >
                {i + 1}
              </span>
              <div className="bg-white border border-slate-200 rounded-2xl p-4 md:p-5">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-slate-100 grid place-items-center text-[#0F2C59] shrink-0">
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-display font-bold text-[#0F2C59] leading-tight">{title}</h3>
                    <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{text}</p>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ol>

        <div className="mt-8 flex justify-center">
          <Link
            to="/contact#programare"
            className="inline-flex items-center justify-center gap-2 bg-[#B4262A] text-white rounded-full px-6 py-3.5 text-sm sm:text-base font-semibold hover:bg-[#8E1D20] transition-colors"
            data-testid="how-treatment-appointment-btn"
          >
            <CalendarClock className="w-4 h-4" /> Solicită o programare
          </Link>
        </div>
      </section>

      {/* TECHNOLOGY — patient-benefit oriented, replaces equipment-list style */}
      <section className="bg-[#F8FAFC] border-y border-slate-200 py-16 md:py-24" data-testid="technology-section" aria-labelledby="technology-heading">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="max-w-3xl">
            <div className="text-[#D97757] text-sm font-semibold uppercase tracking-widest">Tehnologie</div>
            <h2 id="technology-heading" className="mt-3 font-display font-extrabold text-3xl md:text-4xl text-[#0F2C59] leading-tight">
              Tehnologie digitală pentru diagnostic și tratament
            </h2>
            <p className="mt-4 text-slate-600 text-base md:text-lg leading-relaxed">
              Tehnologia este utilă atunci când contribuie la un diagnostic mai bine documentat, la planificarea tratamentului sau la realizarea anumitor etape cu mai multe informații disponibile medicului.
            </p>
            <p className="mt-3 text-slate-600 text-base md:text-lg leading-relaxed">
              La Denti Service, utilizarea echipamentelor digitale este integrată în funcție de indicația medicală și de necesitățile fiecărui caz.
            </p>
            <p className="mt-3 text-slate-600 text-base md:text-lg leading-relaxed">
              Pentru curățarea profesională a dinților, aflați mai multe despre <Link to="/igienizare-dentara-ems" className="text-[#0F2C59] font-semibold underline hover:text-[#B4262A]" data-testid="home-technology-igienizare-link">Detartraj și igienizare dentară în Bacău</Link>.
            </p>
            <p className="mt-3 text-slate-600 text-base md:text-lg leading-relaxed">
              Pentru situații acute, consultați pagina despre <Link to="/urgente-stomatologice" className="text-[#0F2C59] font-semibold underline hover:text-[#B4262A]" data-testid="home-technology-urgente-link">urgențe stomatologice în Bacău</Link>.
            </p>
            <p className="mt-3 text-slate-600 text-base md:text-lg leading-relaxed">
              Pentru alinierea dinților, vedeți opțiunile de <Link to="/ortodontie" className="text-[#0F2C59] font-semibold underline hover:text-[#B4262A]" data-testid="home-technology-ortodontie-link">aparat dentar în Bacău</Link>.
            </p>
          </div>

          <div className="mt-10 grid gap-4 md:gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { icon: Scan, title: "Diagnostic și imagistică", text: "Radiologia digitală și investigațiile CBCT pot oferi informații detaliate despre dinți, os și structurile anatomice atunci când sunt necesare pentru diagnostic și planificare." },
              { icon: Cpu, title: "Scanare intraorală și flux digital", text: "Scanarea intraorală poate fi utilizată în anumite situații pentru înregistrarea digitală a arcadelor dentare și integrarea cazului într-un flux de lucru digital." },
              { icon: Layers, title: "Restaurări dentare CAD/CAM", text: "Tehnologiile CAD/CAM pot fi utilizate pentru proiectarea și realizarea digitală a anumitor restaurări dentare, în funcție de indicația clinică și de particularitățile fiecărui caz." },
              { icon: Microscope, title: "Microscop dentar", text: "Magnificația și iluminarea oferite de microscop pot ajuta medicul să observe detalii dificil de vizualizat cu ochiul liber, fiind utile în special în anumite proceduri endodontice și restaurative." },
              { icon: Waves, title: "Laser și igienizare profesională", text: "Tehnologiile laser și sistemele profesionale de igienizare pot fi utilizate pentru anumite indicații, în funcție de situația clinică." },
              { icon: Activity, title: "Analiză funcțională", text: "În cazurile în care este relevantă, analiza digitală a mișcărilor mandibulei poate oferi informații suplimentare pentru evaluarea funcțională și planificarea tratamentului." },
            ].map(({ icon: Icon, title, text }) => (
              <div key={title} className="group bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-md hover:-translate-y-0.5 transition-all" data-testid={`tech-benefit-${title.split(' ')[0].toLowerCase()}`}>
                <div className="w-12 h-12 rounded-xl bg-slate-100 grid place-items-center text-[#0F2C59]">
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="mt-4 font-display font-bold text-[#0F2C59] leading-tight">{title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 md:mt-10 bg-white border border-slate-200 rounded-2xl p-5 md:p-6 flex items-start gap-3" data-testid="tech-important-note">
            <ShieldCheck className="w-5 h-5 text-[#0F2C59] mt-0.5 shrink-0" />
            <p className="text-sm md:text-base text-slate-700 leading-relaxed">
              <strong>Notă importantă:</strong> tehnologia nu înlocuiește consultația și decizia medicului. Rolul ei este să ofere informații suplimentare care pot susține diagnosticul și planificarea tratamentului.
            </p>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/diagnostic-digital-integrat"
              className="inline-flex items-center justify-center gap-2 bg-[#0F2C59] text-white rounded-full px-6 py-3.5 text-sm sm:text-base font-semibold hover:bg-[#163d7a] transition-colors"
              data-testid="tech-diagnostic-btn"
            >
              Descoperă diagnosticul digital integrat <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* BRAND / TECHNOLOGY PARTNERS
          Text wordmarks are placeholders until official press-kit files
          are dropped into /app/frontend/public/logos/. No manufacturer
          logo has been recreated or generated. */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-14 md:py-20" data-testid="brand-partners-section" aria-labelledby="brand-partners-heading">
        <div className="max-w-3xl">
          <div className="text-[#D97757] text-sm font-semibold uppercase tracking-widest">Parteneri tehnologici</div>
          <h2 id="brand-partners-heading" className="mt-3 font-display font-extrabold text-3xl md:text-4xl text-[#0F2C59] leading-tight">
            Tehnologie și sisteme de încredere
          </h2>
          <p className="mt-4 text-slate-600 text-base md:text-lg leading-relaxed">
            Folosim sisteme și tehnologii recunoscute internațional pentru implantologie, restaurări dentare într-o zi, ortodonție, tratamente laser, igienizare profesională și imagistică digitală.
          </p>
        </div>
        <BrandLogos brands={HOMEPAGE_BRANDS} variant="homepage" />
      </section>

      {/* CEREC — restored after Technology + BrandPartners per approved section order */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-8 md:py-14 grid gap-6 md:gap-10 lg:grid-cols-2 items-center" data-testid="cerec-section">
        <div className="rounded-3xl overflow-hidden w-full aspect-[4/3] bg-slate-100 shadow-sm order-2 lg:order-1 flex items-center justify-center">
          <picture>
            <source
              type="image/webp"
              srcSet={`${process.env.REACT_APP_BACKEND_URL}/api/media/service-diagnostic-digital.webp`}
            />
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}/api/media/service-diagnostic-digital.jpeg`}
              alt="Flux digital CEREC · scanare, design și frezare în clinică"
              width="768"
              height="768"
              loading="lazy"
              decoding="async"
              className="aspect-square h-full w-auto max-w-full object-contain"
            />
          </picture>
        </div>
        <div className="order-1 lg:order-2">
          <div className="text-[#D97757] text-xs sm:text-sm font-semibold uppercase tracking-widest">Stomatologie digitală · CEREC</div>
          <h2 className="mt-3 font-display font-extrabold text-2xl sm:text-3xl md:text-4xl text-[#0F2C59] leading-tight">Coroană dentară într-o zi</h2>
          <p className="mt-3 text-sm md:text-base text-slate-600 leading-relaxed">
            În cazurile eligibile, coroana dentară poate fi realizată într-o singură zi. Folosim tehnologia CEREC — un sistem digital de origine germană: dintele este scanat digital, restaurarea este proiectată digital, iar coroana poate fi produsă în clinică. Eligibilitatea este stabilită de medic după consultație și investigațiile necesare.
          </p>
          <div className="mt-6 grid grid-cols-3 gap-3">
            {[
              { step: "01", label: "Scanare digitală" },
              { step: "02", label: "Design digital" },
              { step: "03", label: "Realizare în clinică" },
            ].map((s) => (
              <div key={s.step} className="rounded-xl bg-white border border-slate-200 p-3">
                <div className="text-[10px] uppercase tracking-widest text-[#14B8A6] font-bold">{s.step}</div>
                <div className="mt-1 text-xs text-slate-700 font-medium">{s.label}</div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-sm text-slate-500">Coordonator CEREC: <span className="font-semibold text-[#0F2C59]">Dr. Cruceanu Nicoleta</span></p>
          <Link to="/coroane-dentare-cerec" className="inline-flex items-center gap-2 mt-6 text-[#0F2C59] font-semibold hover:underline" data-testid="link-cerec">
            Vezi cum funcționează CEREC <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* SIMPLE CASE OR COMPLEX CASE — new patient-education section */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-8 md:py-14" data-testid="case-complexity-section" aria-labelledby="case-complexity-heading">
        <div className="grid gap-8 lg:grid-cols-5 items-start">
          <div className="lg:col-span-2">
            <div className="text-[#D97757] text-sm font-semibold uppercase tracking-widest">Complexitatea cazului</div>
            <h2 id="case-complexity-heading" className="mt-3 font-display font-extrabold text-3xl md:text-4xl text-[#0F2C59] leading-tight">
              Caz simplu sau caz complex?
            </h2>
          </div>
          <div className="lg:col-span-3 space-y-3 text-slate-600 text-base md:text-lg leading-relaxed">
            <p>
              Nu fiecare problemă dentară necesită un plan de tratament complex. Uneori, o problemă poate fi rezolvată punctual, printr-o procedură clar delimitată.
            </p>
            <p>
              În alte situații — dinți lipsă, lucrări dentare vechi extinse, probleme gingivale, uzură dentară, modificări ale mușcăturii sau combinații ale acestora — poate deveni importantă ordinea în care sunt realizate tratamentele.
            </p>
            <p>
              Un caz complex poate necesita evaluarea mai multor aspecte înainte de începerea restaurărilor definitive: starea dinților rămași, osul, gingiile, mușcătura, lucrările existente și relația dintre diferitele etape de tratament.
            </p>
            <p>
              În astfel de situații, colaborarea între specialități și planificarea etapelor înainte de începerea tratamentului pot ajuta la construirea unui plan realist și coerent pentru pacient.
            </p>

            <div className="mt-5 bg-slate-50 border border-slate-200 rounded-2xl p-4 md:p-5 flex items-start gap-3" data-testid="case-complexity-highlight">
              <CheckCircle2 className="w-5 h-5 text-[#14B8A6] mt-0.5 shrink-0" />
              <p className="text-sm md:text-base text-slate-700 leading-relaxed">
                Scopul nu este complicarea tratamentului, ci stabilirea unei succesiuni potrivite situației clinice.
              </p>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/cazuri-tratate"
                className="inline-flex items-center justify-center gap-2 bg-[#0F2C59] text-white rounded-full px-6 py-3.5 text-sm sm:text-base font-semibold hover:bg-[#163d7a] transition-colors"
                data-testid="case-complexity-cases-btn"
              >
                Vezi cazuri tratate <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-8 md:py-14" data-testid="team-section">
        <div className="text-[#D97757] text-sm font-semibold uppercase tracking-widest">Echipa medicală</div>
        <div className="mt-3 flex items-end justify-between gap-6 flex-wrap">
          <h2 className="font-display font-extrabold text-3xl md:text-4xl text-[#0F2C59]">Echipa clinicii</h2>
          <Link to="/echipa-medicala" className="inline-flex items-center gap-1 text-[#0F2C59] font-semibold hover:underline" data-testid="link-team">
            Vezi toată echipa <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {doctors.slice(0, 6).map((d) => {
            // Derive a WebP variant URL by swapping the file extension. Every
            // team photo in /app/backend/uploads/ has both a .jpg/.png/.jpeg
            // fallback AND a matching .webp file (see backend uploads dir).
            // Modern browsers pick the ~15–25 KB WebP; legacy browsers fall
            // back to the original file via the <img> src. No duplicate
            // downloads: <picture> selects exactly one <source>.
            const rawUrl = d.photo_url || "";
            const abs = rawUrl.startsWith("http") ? rawUrl : `${process.env.REACT_APP_BACKEND_URL}${rawUrl}`;
            const webpUrl = abs.replace(/\.(png|jpe?g)$/i, ".webp");
            const hasWebpVariant = /\.(png|jpe?g)$/i.test(abs);
            return (
            <div key={d.id} className="bg-white border border-slate-200 rounded-2xl p-5 hover:border-[#0F2C59] transition-colors" data-testid={`doctor-card-${d.id}`}>
              {d.photo_url
                ? (
                  <picture>
                    {hasWebpVariant && <source type="image/webp" srcSet={webpUrl} />}
                    <img src={abs} alt={d.name} width="400" height="400" className="w-full aspect-square rounded-2xl object-cover" style={{ objectPosition: "center 15%" }} loading="lazy" decoding="async" />
                  </picture>
                )
                : <InitialsAvatar name={d.name} aspect="aspect-square" />}
              <h3 className="mt-4 font-display font-bold text-lg text-[#0F2C59] leading-tight">{d.name}</h3>
              {d.name === "Cruceanu Raluca" && (
                <p className="mt-2 text-sm font-semibold text-[#0F2C59] leading-snug" data-testid={`home-doctor-${d.id}-tagline`}>
                  Dr. Raluca Cruceanu știe foarte bine lasere și este pasionată de ele.
                </p>
              )}
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{d.role}</p>
            </div>
            );
          })}
        </div>
      </section>

      {/* PATIENTS FROM ABROAD */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-8 md:py-14 grid gap-10 lg:grid-cols-2 items-center" data-testid="abroad-section">
        <div>
          <div className="text-[#D97757] text-sm font-semibold uppercase tracking-widest">Pacienți din străinătate</div>
          <h2 className="mt-3 font-display font-extrabold text-3xl md:text-4xl text-[#0F2C59]">Români din diaspora</h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Primul pas este consultația în Bacău. Investigațiile pot fi realizate în clinică, iar tratamentele se organizează în etape. Durata exactă depinde de fiecare caz.
          </p>
          <Link to="/pacienti-din-strainatate" className="inline-flex items-center gap-2 mt-6 text-[#0F2C59] font-semibold hover:underline" data-testid="link-abroad">
            Cum funcționează <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <BrandedVisual variant="diaspora" aspect="aspect-[4/3]" label="Pacienți români din Europa · flux consultație → tratament în Bacău" />
      </section>

      {/* REVIEWS — moved above EU project per approved section order */}
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-8 md:py-14" data-testid="home-reviews-wrap">
        <Suspense fallback={<div style={{ minHeight: 400 }} aria-hidden="true" />}>
          <ReviewsSection />
        </Suspense>
      </div>

      {/*
        European-funded project — concise homepage summary. Full details,
        official contact and both PDF releases live at /proiecte-europene.
      */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 mt-4 md:mt-8" data-testid="home-eu-project">
        <div className="bg-slate-50 border border-slate-200 rounded-3xl p-6 md:p-10">
          <div className="text-[10px] md:text-xs font-bold uppercase tracking-widest text-[#14B8A6]">Finanțare europeană · 02.04.2026</div>
          <h2 className="mt-3 font-display font-extrabold text-2xl md:text-3xl text-[#0F2C59]">
            Proiecte cu finanțare europeană
          </h2>
          <p className="mt-3 text-slate-700 leading-relaxed max-w-3xl">
            DENTI SERVICE S.R.L. a implementat un proiect de modernizare și dezvoltare a capacității de prestare a serviciilor stomatologice, finanțat prin Programul Regional Nord-Est 2021–2027.
          </p>

          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            <div className="bg-white border border-slate-200 rounded-2xl p-4">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Valoare totală proiect</div>
              <div className="mt-1 font-display font-extrabold text-[#0F2C59] text-lg">1.498.891,44 lei</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-4">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Finanțare FEDR/FSE+/FC/FTJ</div>
              <div className="mt-1 font-display font-extrabold text-[#0F2C59] text-lg">845.818,00 lei</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-4">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Contribuție buget național</div>
              <div className="mt-1 font-display font-extrabold text-[#0F2C59] text-lg">149.262,00 lei</div>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/proiecte-europene" className="inline-flex items-center gap-2 rounded-full bg-[#0F2C59] text-white px-5 py-3 text-sm font-semibold hover:bg-[#163d7a] transition-colors" data-testid="home-eu-details-btn">
              Citește comunicatul complet
            </Link>
            <a href="/documents/comunicat-denti-service-02-04-2026.pdf" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border-2 border-[#0F2C59] text-[#0F2C59] px-5 py-3 text-sm font-semibold hover:bg-slate-50 transition-colors" data-testid="home-eu-pdf-btn">
              Descarcă comunicatul din 02.04.2026
            </a>
          </div>
          <p className="mt-4 text-xs text-slate-600">
            Contract de finanțare nr. 276/29.08.2024 · Programul Regional Nord-Est 2021–2027, Prioritatea PRNE_P1.
          </p>
        </div>
      </section>

      {/* FINAL CTA — refined per approved home structure */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 mt-12 md:mt-20 mb-16 md:mb-24" data-testid="home-final-cta" aria-labelledby="home-final-cta-heading">
        <div className="bg-[#0F2C59] text-white rounded-3xl p-6 sm:p-10 md:p-14 relative overflow-hidden">
          <div aria-hidden="true" className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-30"
               style={{ background: "radial-gradient(circle, rgba(20,184,166,0.55) 0%, transparent 60%)" }} />
          <div className="relative max-w-3xl">
            <div className="text-[#5EEAD4] text-xs sm:text-sm font-semibold uppercase tracking-widest">Consultație</div>
            <h2 id="home-final-cta-heading" className="mt-3 font-display font-extrabold text-3xl md:text-4xl leading-tight">
              Ai nevoie de o evaluare stomatologică?
            </h2>
            <p className="mt-4 text-white/85 text-base md:text-lg leading-relaxed">
              Primul pas este consultația, în cadrul căreia medicul poate evalua situația și poate stabili investigațiile și opțiunile de tratament potrivite cazului tău.
            </p>

            <div className="mt-6 grid gap-2 text-sm md:text-base text-white/90">
              <div className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 shrink-0 text-[#5EEAD4]" /><span><strong>Denti Service</strong> · Strada Ștefan cel Mare nr. 34, Bacău</span></div>
              <div className="flex items-center gap-2"><Clock className="w-4 h-4 shrink-0 text-[#5EEAD4]" /><span>Luni–Vineri: 08:00–20:00</span></div>
            </div>

            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/contact#programare"
                className="inline-flex items-center justify-center gap-2 bg-[#B4262A] text-white rounded-full px-6 py-3.5 text-sm sm:text-base font-semibold hover:bg-[#8E1D20] transition-colors"
                data-testid="home-final-appointment-btn"
              >
                <CalendarClock className="w-4 h-4" /> Solicită o programare
              </Link>
              <a
                href={CLINIC.phoneTel}
                className="inline-flex items-center justify-center gap-2 bg-white text-[#0F2C59] rounded-full px-6 py-3.5 text-sm sm:text-base font-semibold hover:bg-slate-100 transition-colors"
                data-testid="home-final-call-btn"
              >
                <Phone className="w-4 h-4" /> Sună acum
              </a>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
